import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import type { Variants } from "framer-motion";
import { Info, Play } from "lucide-react";
import { useCascadeFadeIn } from "../../hooks";
import { pageTransitions } from "../../transitions/pageTransitions";
import { NewBadge } from "../NewBadge";

interface TitleCardProps {
  readonly title: ApiTitle;
  readonly index?: number;
  readonly className?: string;
  readonly progress?: {
    assetId: string;
    progressPercent: number;
    positionSec: number;
    durationSec: number;
  };
  readonly transitionTo: (path: string, variant?: Variants) => void;
  readonly onAction?: () => void;
  readonly onInfo?: () => void;
  readonly hideActions?: boolean;
}

/**
 * Card overlay content - shown on hover (hidden when hideActions=true)
 */
function CardOverlay({
  title,
  onPlay,
  onMoreInfo,
}: {
  readonly title: ApiTitle;
  readonly onPlay: () => void;
  readonly onMoreInfo: () => void;
}) {
  return (
    <div className="absolute inset-0 flex flex-col justify-end p-4 opacity-0 group-hover:opacity-100 group-[.rail-focused]:opacity-100 transition-opacity duration-300 z-10">
      <div className="space-y-2">
        <h2 className="font-semibold text-white text-2xl leading-tight line-clamp-2">{title.name}</h2>

        <div className="flex items-center gap-1.5 text-xs">
          {title.year && <span className="text-neutral-300 font-medium">{title.year}</span>}
          {title.year && title.runtimeMinutes && <span className="text-neutral-500">•</span>}
          {title.runtimeMinutes && <span className="text-neutral-300 font-medium">{title.runtimeMinutes}m</span>}
          {(title.year || title.runtimeMinutes) && title.genres && title.genres.length > 0 && (
            <span className="text-neutral-500">•</span>
          )}
          {title.genres && title.genres.length > 0 && (
            <span className="text-neutral-400 truncate">{title.genres.slice(0, 2).join(", ")}</span>
          )}
        </div>

        {title.directorName && (
          <p className="text-xs text-neutral-400 truncate">
            Directed by <span className="text-neutral-300 font-medium truncate">{title.directorName}</span>
          </p>
        )}

        <div className="flex gap-0 justify-start pt-1 pointer-events-none">
          <Button variant="ghost-sm" className="pl-0 pointer-events-auto" onClick={onPlay}>
            <Play size={16} />
            <span className="ml-1.5 text-sm font-semibold">Play</span>
          </Button>
          <Button variant="ghost-sm" className="pl-0 pointer-events-auto" onClick={onMoreInfo}>
            <Info size={16} />
            <span className="ml-1.5 text-sm font-semibold">More Info</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TitleCard({
  title,
  index = 0,
  progress,
  className = "",
  onAction,
  onInfo,
  transitionTo,
  hideActions = false,
}: TitleCardProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index });

  const handlePlay = () => {
    if (progress) {
      // Resume playback: Navigate directly to player with assetId
      transitionTo(`/home/watch/${title.slug}?assetId=${progress.assetId}`, pageTransitions.cinematicFade);
    } else {
      // Play from beginning (global pointerdown listener exits nav mode)
      transitionTo(`/home/watch/${title.slug}`, pageTransitions.cinematicFade);
    }
  };

  const handleMoreInfo = () => {
    transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
  };

  // Use callback props if provided, otherwise use internal handlers
  const executeAction = onAction ?? handlePlay;
  const executeInfo = onInfo ?? handleMoreInfo;

  // Handle click on the entire card when actions are hidden (browse-only mode)
  const handleCardClick = () => {
    if (hideActions) {
      handleMoreInfo(); // Navigate to title detail page
    }
  };

  // Handle keyboard activation for browse-only mode
  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (hideActions && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleMoreInfo();
    }
  };

  return (
    <div //NOSONAR
      ref={ref}
      aria-label={`${title.name} - ${progress ? "Continue watching" : "Play"}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={hideActions ? "button" : undefined}
      tabIndex={hideActions ? 0 : undefined} //NOSONAR
      className={`
        group flex-shrink-0 self-start text-left w-[calc(100vw-3rem)] sm:w-[400px] snap-center
        transition-all duration-500 ease-out
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
        ${hideActions ? "cursor-pointer" : ""}
        ${className}
      `}
    >
      {/* Outer wrapper with shadow - enhanced on hover and focus */}
      <div className="card-base card-hover-vertical">
        <div className="relative aspect-[2/3] overflow-hidden rounded-lg bg-neutral-900 ring-1 ring-neutral-800/50">
          {/* NEW badge for recently added titles */}
          {title.createdAt ? <NewBadge createdAt={title.createdAt} /> : null}

          {/* Poster image */}
          <img
            src={title.verticalTileUrl ?? title.posterUrlHd ?? undefined}
            alt={title.name}
            className="h-full w-full object-cover"
          />

          {/* Progress bar overlay for continue watching */}
          {progress && (
            <div className="absolute bottom-0 left 0 right-0 h-1 bg-neutral-800 z-20">
              <div
                className="h-full bg-red-600 transition-all"
                style={{ width: `${progress.progressPercent}%` }}
                aria-label={`${progress.progressPercent}% watched`}
              />
            </div>
          )}

          {/* Diagonal gradient overlays on hover - dark from bottom-left, light from top-right */}
          {/* No rotation/scaling on touch devices to prevent overflow */}
          <div className="card-gradient-light" />
          <div className="card-gradient-dark" />

          {/* Content overlay (hidden when hideActions=true) */}
          {!hideActions && <CardOverlay title={title} onPlay={executeAction} onMoreInfo={executeInfo} />}
        </div>
      </div>
    </div>
  );
}
